import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Modal,
  Tooltip,
  Space,
  Descriptions,
  Tag,
  DatePicker,
  InputNumber,
  Upload,
  message,
  Image,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleFilled,
  SearchOutlined,
  UploadOutlined,
  CalendarOutlined,
  DeleteFilled,
} from "@ant-design/icons";
import moment from "moment-timezone";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

// FREE AIRPORT DATABASE
const AIRPORTS_JSON_URL =
  "https://raw.githubusercontent.com/mwgg/Airports/master/airports.json";

const AdminProductsPage = () => {
  const { request } = useRequest();

  // Data
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [allAirports, setAllAirports] = useState({});
  const [airportMap, setAirportMap] = useState({}); // IATA → full airport
  const [countries, setCountries] = useState([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // Image
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  // Search & Filter
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Modal Form State - For multi-select countries and airports
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [availableAirports, setAvailableAirports] = useState([]);
  const [selectedAirports, setSelectedAirports] = useState([]);

  // Load Countries
  const fetchCountries = async () => {
    try {
      const res = await fetch("https://restcountries.com/v3.1/all?fields=name,cca2");
      const data = await res.json();
      const sorted = data
        .map((d) => ({ name: d.name.common, code: d.cca2 }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCountries(sorted);
    } catch (e) {
      ShowToast("Failed to load countries", Severty.ERROR);
    }
  };

  // Load Full Airport DB
  const loadAllAirports = async () => {
    try {
      const res = await fetch(AIRPORTS_JSON_URL);
      if (!res.ok) throw new Error();
      const data = await res.json();

      const iataMap = {};
      Object.values(data).forEach((a) => {
        if (a.iata) iataMap[a.iata] = a;
      });

      setAllAirports(data);
      setAirportMap(iataMap);
    } catch (e) {
      ShowToast("Airport DB failed", Severty.ERROR);
    }
  };

  // Filter Airports by Selected Countries
  const fetchAirportsByCountries = (countryNames) => {
    if (!countryNames || countryNames.length === 0 || !allAirports) {
      setAvailableAirports([]);
      setSelectedAirports([]);
      form.setFieldsValue({ airports: [] });
      return;
    }
    
    // Convert country names to country codes
    const countryCodes = countryNames.map(countryName => {
      const country = countries.find(c => c.name === countryName);
      return country ? country.code : null;
    }).filter(code => code !== null);

    const filtered = Object.values(allAirports)
      .filter((a) => countryCodes.includes(a.country) && a.iata && a.type !== "closed")
      .map((a) => ({
        iata: a.iata,
        name: a.name,
        city: a.city || "Unknown",
        country: a.country,
        countryName: countries.find(c => c.code === a.country)?.name || a.country
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    setAvailableAirports(filtered);
  };

  // Fetch Products
  const fetchProducts = (page = 1, size = 10, filters = {}) => {
    setLoading(true);
    let qp = `page=${page}&page_size=${size}`;
    if (filters.searchKeyword?.trim()) qp += `&search=${encodeURIComponent(filters.searchKeyword.trim())}`;
    if (filters.status && filters.status !== "all") qp += `&status=${filters.status}`;
    if (filters.dateRange?.length === 2) {
      qp += `&start_date=${filters.dateRange[0].format("YYYY-MM-DD")}&end_date=${filters.dateRange[1].format("YYYY-MM-DD")}`;
    }

    request({
      url: `/service/product_list?${qp}`,
      method: "GET",
      onSuccess: (res) => {
        const updated = (res?.data || []).map((p) => ({
          ...p,
          activePriceInfo: {
            price: p.active_price || "$0.00",
            startDate: formatDate(p.active_start_date),
            endDate: formatDate(p.active_end_date),
          },
        }));
        setProducts(updated);
        setPagination({
          current: res?.pagination?.current_page || 1,
          pageSize: res?.pagination?.page_size || 10,
          total: res?.pagination?.total_count || 0,
        });
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
  };

  // Fetch Services
  const fetchServices = () => {
    request({
      url: "/service/service_list",
      method: "GET",
      onSuccess: ({ data }) => setServices(data || []),
    });
  };

  // Country → Airport handlers
  const handleCountriesChange = (values) => {
    setSelectedCountries(values);
    setSelectedAirports([]);
    form.setFieldsValue({ airports: [], price_models: [] });
    fetchAirportsByCountries(values);
  };

  const handleAirportsChange = (values) => {
    setSelectedAirports(values);
  };

  // Initial Load
  useEffect(() => {
    fetchCountries();
    loadAllAirports();
    fetchProducts();
    fetchServices();
  }, []);

  // Image Upload (unchanged)
  const handleImageUpload = async (file) => {
    return new Promise((resolve, reject) => {
      setUploadLoading(true);
      const formData = new FormData();
      formData.append("image", file);

      request({
        url: "/auth/image_upload",
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        onSuccess: (res) => {
          let url = res?.image_url || res?.data?.image_url || res?.data?.data?.image_url;
          if (url) {
            setUploadedImageUrl(url);
            setImagePreview(URL.createObjectURL(file));
            message.success("Image uploaded");
            setUploadLoading(false);
            resolve(false);
          } else {
            throw new Error("No URL");
          }
        },
        onError: (e) => {
          message.error("Upload failed");
          setUploadLoading(false);
          reject(e);
        },
      });
    });
  };

  const uploadProps = {
    beforeUpload: async (file) => {
      const isImg = ["image/jpeg", "image/png", "image/jpg"].includes(file.type);
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isImg || !isLt5M) {
        message.error(isImg ? "Image < 5MB" : "JPG/PNG only");
        return false;
      }
      try {
        await handleImageUpload(file);
        return false;
      } catch {
        return false;
      }
    },
    showUploadList: false,
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setUploadedImageUrl(null);
    form.setFieldsValue({ image: null });
  };

  // Search & Filters (unchanged)
  const handleSearch = (kw) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const t = setTimeout(() => {
      setSearchKeyword(kw);
      fetchProducts(1, pagination.pageSize, { searchKeyword: kw, status: statusFilter, dateRange });
    }, 500);
    setSearchTimeout(t);
  };

  const handleSearchChange = (e) => handleSearch(e.target.value);
  const handleSearchSubmit = (v) => {
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchKeyword(v.keyword);
    setDateRange(v.date_range);
    fetchProducts(1, pagination.pageSize, { searchKeyword: v.keyword, status: statusFilter, dateRange: v.date_range });
  };

  const handleStatusFilterChange = (v) => {
    const s = v === "active" ? "1" : v === "inactive" ? "0" : "all";
    setStatusFilter(s);
    fetchProducts(1, pagination.pageSize, { searchKeyword, status: s, dateRange });
  };

  const handleDateRangeChange = (d) => {
    setDateRange(d);
    fetchProducts(1, pagination.pageSize, { searchKeyword, status: statusFilter, dateRange: d });
  };

  const resetFilters = () => {
    setSearchKeyword("");
    setStatusFilter("all");
    setDateRange(null);
    searchForm.resetFields();
    if (searchTimeout) clearTimeout(searchTimeout);
    fetchProducts();
  };

  useEffect(() => {
    return () => searchTimeout && clearTimeout(searchTimeout);
  }, [searchTimeout]);

  // Format (unchanged)
  const formatDate = (d) =>
    !d ? "N/A" : new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  const getRelativeTime = (d) => {
    if (!d) return "N/A";
    const diff = Date.now() - new Date(d);
    const days = Math.floor(diff / 86400000);
    if (days === 0) return `${Math.floor(diff / 60000)} min ago`;
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} wk ago`;
    return `${Math.floor(days / 30)} mo ago`;
  };

  // Open Modal (Add & Edit) - UPDATED
  const openProductModal = (product = null) => {
  setEditingProduct(product);
  setImageFile(null);
  setImagePreview(null);
  setUploadedImageUrl(null);
  setSelectedCountries([]);
  setSelectedAirports([]);
  setAvailableAirports([]);
  form.resetFields();

  if (product) {
    // For editing, populate countries and airports from product data
    const productCountries = product.countries || [];
    const productAirports = product.airports || [];
    
    // Transform price models for the form
    const formattedPriceModels = (product.price_models || []).map(pm => ({
      ...pm,
      validity: pm.price_validity_start && pm.price_validity_end ? [
        moment(pm.price_validity_start),
        moment(pm.price_validity_end)
      ] : null
    }));

    form.setFieldsValue({
      ...product,
      service: product.service,
      countries: productCountries,
      airports: productAirports,
      price_models: formattedPriceModels,
    });
    
    if (product.image_url) {
      setImagePreview(product.image_url);
      setUploadedImageUrl(product.image_url);
    }

    // Rehydrate countries & airports
    setSelectedCountries(productCountries);
    if (productCountries.length > 0) {
      fetchAirportsByCountries(productCountries);
    }
    setSelectedAirports(productAirports);
  } else {
    form.setFieldsValue({ status: true, price_models: [] });
  }
  setProductModalVisible(true);
};

  // Submit - UPDATED for simplified pricing
  const onProductSubmit = (values) => {
  setLoading(true);
  
  const payload = {
    ...values,
    status: editingProduct ? (values.status ? 1 : 0) : 1,
    countries: selectedCountries,
    airports: selectedAirports,
  };

  if (uploadedImageUrl) payload.image_url = uploadedImageUrl;

  // Transform price models - handle date conversion
  if (values.price_models?.length > 0) {
    payload.price_models = values.price_models.map((m) => {
      const priceModel = {
        moq: 1,
        price: parseFloat(m.price) || 0,
      };

      // Handle date conversion if validity range exists
      if (m.validity && m.validity.length === 2) {
        priceModel.price_validity_start = moment.utc(m.validity[0]).toISOString();
        priceModel.price_validity_end = moment.utc(m.validity[1]).toISOString();
      }

      return priceModel;
    });
  }

  const call = editingProduct
    ? request({
        url: `/service/product_update/${editingProduct.id}`,
        method: "PUT",
        data: payload,
        onSuccess: () => {
          ShowToast("Product updated", Severty.SUCCESS);
          setProductModalVisible(false);
          fetchProducts(pagination.current, pagination.pageSize);
          setLoading(false);
        },
        onError: (e) => {
          ShowToast(e?.response?.data?.message || "Failed", Severty.ERROR);
          setLoading(false);
        },
      })
    : request({
        url: "/service/create_product",
        method: "POST",
        data: payload,
        onSuccess: () => {
          ShowToast("Product created", Severty.SUCCESS);
          setProductModalVisible(false);
          fetchProducts(pagination.current, pagination.pageSize);
          setLoading(false);
        },
        onError: (e) => {
          ShowToast(e?.response?.data?.message || "Failed", Severty.ERROR);
          setLoading(false);
        },
      });
};

  // Status Toggle (unchanged)
  const toggleProductStatus = (r) => {
    const s = r.status === 1 ? 0 : 1;
    request({
      url: `/service/product_status_update`,
      method: "PUT",
      data: { id: r.id, status: s },
      onSuccess: () => {
        ShowToast("Status updated", Severty.SUCCESS);
        fetchProducts(pagination.current, pagination.pageSize);
      },
      onError: () => ShowToast("Failed", Severty.ERROR),
    });
  };

  // View (unchanged)
  const openViewModal = (p) => {
    setViewLoading(true);
    setViewingProduct(null);
    setViewModalVisible(true);
    request({
      url: `/service/product_detail?id=${p.id}`,
      method: "GET",
      onSuccess: (res) => {
        setViewingProduct(res?.data || p);
        setViewLoading(false);
      },
      onError: () => {
        setViewingProduct(p);
        setViewLoading(false);
      },
    });
  };

  // Delete (unchanged)
  const openDeleteModal = (p) => { setDeletingProduct(p); setDeleteModalVisible(true); };
  const closeDeleteModal = () => { setDeleteModalVisible(false); setDeletingProduct(null); setDeleteLoading(false); };
  const handleDeleteProduct = () => {
    if (!deletingProduct) return;
    setDeleteLoading(true);
    request({
      url: `/service/delete_product`,
      method: "DELETE",
      data: { id: deletingProduct.id },
      onSuccess: () => {
        ShowToast("Deleted", Severty.SUCCESS);
        fetchProducts(pagination.current, pagination.pageSize);
        closeDeleteModal();
      },
      onError: () => {
        ShowToast("Failed", Severty.ERROR);
        setDeleteLoading(false);
      },
    });
  };

  // Table (unchanged)
  const productColumns = [
    { title: "Image", dataIndex: "image_url", key: "image_url", width: 80, render: (img) => img ? <Image width={50} height={50} src={img} style={{ objectFit: "cover", borderRadius: 4 }} /> : <div style={{ width: 50, height: 50, background: "#f0f0f0", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>No Img</div> },
    { title: "SKU", dataIndex: "sku_code", key: "sku_code", width: 120 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Service", dataIndex: "service_name", key: "service_name" },
    { title: "MOQ", dataIndex: "minimum_order_quantity", key: "moq", width: 80, render: (m) => m || 1 },
    { title: "Created", dataIndex: "created_at", key: "created", render: (d) => <div><div style={{ fontSize: 12 }}><CalendarOutlined style={{ marginRight: 4 }} /> {formatDate(d)}</div><div style={{ fontSize: 11, color: "#999" }}>{getRelativeTime(d)}</div></div> },
    { title: "Price", dataIndex: "activePriceInfo", key: "price", render: (i) => i.price || "$0.00" },
    { title: "Start", dataIndex: "activePriceInfo", key: "start", render: (i) => i.startDate || "N/A" },
    { title: "End", dataIndex: "activePriceInfo", key: "end", render: (i) => i.endDate || "N/A" },
    { title: "Status", dataIndex: "status", key: "status", render: (s, r) => <Switch checked={s === 1} onChange={() => toggleProductStatus(r)} /> },
    {
      title: "Actions", key: "act", width: 150,
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => openViewModal(r)} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => openProductModal(r)} /></Tooltip>
          <Tooltip title="Delete"><Button danger size="small" icon={<DeleteOutlined />} onClick={() => openDeleteModal(r)} /></Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col><Title level={3} className="m-0">Manage Products</Title></Col>
          <Col><Button type="primary" onClick={() => openProductModal()}>Add Product</Button></Col>
        </Row>

        {/* Search (unchanged) */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form form={searchForm} layout="vertical" onFinish={handleSearchSubmit} initialValues={{ keyword: "", status: "all", date_range: null }}>
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={12} md={8} lg={6}>
                  <Input placeholder="Name, SKU..." prefix={<SearchOutlined />} allowClear onChange={handleSearchChange} />
       
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                  <Select onChange={handleStatusFilterChange}>
                    <Option value="all">All</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                  <RangePicker style={{ width: "100%" }} onChange={handleDateRangeChange} />
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                  <Space>
                    {/* <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Search</Button> */}
                    <Button type="primary" onClick={resetFilters}>Reset</Button>
                  </Space>
              </Col>
            </Row>
          </Form>
          {(searchKeyword || statusFilter !== "all" || dateRange) && (
            <div style={{ marginTop: 16, padding: 8, background: "#f0f8ff", borderRadius: 6 }}>
              <Space wrap>
                <strong>Filters:</strong>
                {searchKeyword && <Tag color="blue">"{searchKeyword}"</Tag>}
                {statusFilter !== "all" && <Tag color="green">{statusFilter === "1" ? "Active" : "Inactive"}</Tag>}
                {dateRange && <Tag color="blue">{dateRange[0].format("YYYY-MM-DD")} to {dateRange[1].format("YYYY-MM-DD")}</Tag>}
                <Button type="link" size="small" onClick={resetFilters}>Clear</Button>
              </Space>
            </div>
          )}
        </Card>

        <Table
          rowKey="id"
          columns={productColumns}
          dataSource={products}
          loading={loading}
          pagination={{ ...pagination, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
          onChange={(p) => fetchProducts(p.current, p.pageSize, { searchKeyword, status: statusFilter, dateRange })}
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* Add/Edit Modal - UPDATED with price models in Product Availability */}
      <Modal open={productModalVisible} onCancel={() => setProductModalVisible(false)} footer={null} width={900}>
        <Title level={4}>{editingProduct ? "Edit" : "Add"} Product</Title>
        <Form form={form} layout="vertical" onFinish={onProductSubmit}>
          {/* Image (unchanged) */}
          <Form.Item label="Image">
            <div>
              {imagePreview && (
                <div style={{ position: "relative", display: "inline-block", marginBottom: 16 }}>
                  <Image width={150} height={150} src={imagePreview} style={{ borderRadius: 8, objectFit: "cover" }} />
                  <Button danger size="small" icon={<DeleteFilled />} style={{ position: "absolute", top: 8, right: 8 }} onClick={handleRemoveImage} />
                </div>
              )}
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} loading={uploadLoading}>
                  {imagePreview ? "Change" : "Upload"} Image
                </Button>
              </Upload>
              <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>JPG, PNG, max 5MB</div>
            </div>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="SKU" name="sku_code">
                <Input placeholder="Auto-generated" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item label="Service" name="service" rules={[{ required: true }]}>
            <Select placeholder="Select service">
              {services.map((s) => (
                <Option key={s.id} value={s.id}>{s.name}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Product Availability with Price Models */}
          <Card title="Product Availability" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label="Countries" 
                  name="countries" 
                  rules={[{ required: true, message: 'Please select at least one country' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select countries"
                    onChange={handleCountriesChange}
                    showSearch
                    filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}
                    value={selectedCountries}
                  >
                    {countries.map((c) => (
                      <Option key={c.code} value={c.name}>{c.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  label="Airports" 
                  name="airports"
                  rules={[{ required: true, message: 'Please select at least one airport' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select airports"
                    onChange={handleAirportsChange}
                    disabled={selectedCountries.length === 0}
                    showSearch
                    filterOption={(i, o) => {
                      if (o.children && o.children.props && o.children.props.children) {
                        const children = o.children.props.children;
                        const airportText = Array.isArray(children) 
                          ? children.map(child => 
                              typeof child === 'string' ? child : 
                              (child.props && child.props.children) || ''
                            ).join(' ')
                          : String(children);
                        return airportText.toLowerCase().includes(i.toLowerCase());
                      }
                      return false;
                    }}
                    notFoundContent={availableAirports.length === 0 ? <Spin size="small" /> : "No airports found"}
                    value={selectedAirports}
                  >
                    {availableAirports.map((a) => (
                      <Option key={a.iata} value={a.iata}>
                        <strong>{a.iata}</strong> – {a.name} ({a.city}, {a.countryName})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            
            {/* Price Models inside Product Availability */}
            <Form.Item 
              label="Price Models"
              tooltip="Set pricing for the selected countries and airports"
            >
              <Form.List name="price_models">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card key={key} style={{ marginBottom: 16, border: "1px solid #d9d9d9" }} 
                        extra={<Button type="link" danger onClick={() => remove(name)}>Remove</Button>}>
                        <Row gutter={16}>
                          <Col span={6}>
                            <Form.Item 
                              {...restField}
                              name={[name, "moq"]} 
                              label="MOQ" 
                              initialValue={1}
                            >
                              <InputNumber min={1} disabled style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item 
                              {...restField}
                              name={[name, "price"]} 
                              label="Price ($)" 
                              rules={[{ required: true, message: 'Please enter price' }]}
                            >
                              <InputNumber 
                                min={0} 
                                step={0.01} 
                                style={{ width: "100%" }} 
                                placeholder="0.00"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item 
                              {...restField}
                              name={[name, "validity"]} 
                              label="Validity" 
                              rules={[{ required: true, message: 'Please select validity period' }]}
                            >
                              <RangePicker 
                                showTime 
                                style={{ width: "100%" }}
                                placeholder={['Start Date', 'End Date']}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Button 
                      type="dashed" 
                      onClick={() => add()} 
                      block 
                      icon={<span>+</span>}
                      style={{ marginTop: 8 }}
                    >
                      Add Price Model
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>

            <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
              Select countries first to see available airports. You can select multiple countries and multiple airports.
            </div>
          </Card>

          {editingProduct && (
            <Form.Item label="Status" name="status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {editingProduct ? "Update" : "Create"} Product
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Modal - UPDATED for simplified data */}
      <Modal open={viewModalVisible} onCancel={() => setViewModalVisible(false)} footer={[<Button key="close" onClick={() => setViewModalVisible(false)}>Close</Button>]} width={700}>
        <Title level={4}>Product Details</Title>
        {viewingProduct && (
          <div style={{ padding: "20px 0" }}>
            {viewingProduct.image_url && (
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <Image width={200} height={200} src={viewingProduct.image_url} style={{ objectFit: "cover", borderRadius: 8 }} />
              </div>
            )}
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="SKU Code" span={2}><strong>{viewingProduct.sku_code}</strong></Descriptions.Item>
              <Descriptions.Item label="Name" span={2}><strong>{viewingProduct.name}</strong></Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>{viewingProduct.description || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Service">{viewingProduct.service_name || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="MOQ">{viewingProduct.minimum_order_quantity || 1}</Descriptions.Item>
              <Descriptions.Item label="Countries" span={2}>
                {(viewingProduct.countries || []).map(country => (
                  <Tag key={country} color="blue" style={{ marginBottom: 4 }}>{country}</Tag>
                ))}
              </Descriptions.Item>
              <Descriptions.Item label="Airports" span={2}>
                {(viewingProduct.airports || []).map(airport => {
                  const ap = airportMap[airport];
                  return (
                    <Tag key={airport} color="green" style={{ marginBottom: 4 }}>
                      {ap ? `${ap.name} (${ap.city})` : airport}
                    </Tag>
                  );
                })}
              </Descriptions.Item>
              <Descriptions.Item label="Created">{formatDate(viewingProduct.created_at)}</Descriptions.Item>
              <Descriptions.Item label="Age">{getRelativeTime(viewingProduct.created_at)}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={viewingProduct.status === 1 ? "green" : "red"}>
                  {viewingProduct.status === 1 ? "Active" : "Inactive"}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
            {viewingProduct.price_models?.length > 0 && (
  <>
    <Title level={4} style={{ marginTop: 24 }}>Price Models</Title>
    <Table
      dataSource={viewingProduct.price_models}
      pagination={false}
      size="small"
      columns={[
        { title: "MOQ", dataIndex: "moq", key: "moq" },
        { title: "Price", dataIndex: "price", key: "price", render: (price) => `$${price}` },
        { 
          title: "Start", 
          dataIndex: "price_validity_start", 
          key: "start", 
          render: (date) => date ? formatDate(date) : "N/A" 
        },
        { 
          title: "End", 
          dataIndex: "price_validity_end", 
          key: "end", 
          render: (date) => date ? formatDate(date) : "N/A" 
        },
      ]}
    />
  </>
)}
          </div>
        )}
      </Modal>

      {/* Delete Modal - UPDATED */}
      <Modal
        open={deleteModalVisible}
        onCancel={closeDeleteModal}
        footer={[
          <Button key="cancel" onClick={closeDeleteModal}>Cancel</Button>,
          <Button key="delete" type="primary" danger loading={deleteLoading} onClick={handleDeleteProduct}>Delete</Button>,
        ]}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <ExclamationCircleFilled style={{ color: "#faad14", marginRight: 8 }} />
          <strong>Delete Product</strong>
        </div>
        <p>Are you sure you want to delete this product?</p>
        {deletingProduct && (
          <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 6 }}>
            <div><strong>SKU:</strong> {deletingProduct.sku_code}</div>
            <div><strong>Name:</strong> {deletingProduct.name}</div>
            <div><strong>Countries:</strong> {(deletingProduct.countries || []).join(", ")}</div>
            <div><strong>Airports:</strong> {(deletingProduct.airports || []).map(airport => {
              const ap = airportMap[airport];
              return ap ? `${ap.name} (${ap.city})` : airport;
            }).join(", ")}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminProductsPage;